import { defineStore } from 'pinia';
import { GET_KILLSWITCH_CONFIG, GET_USER_REGISTRIES, UPDATE_DEFAULT_REGISTRY } from '~/types/actions';
import {
  SET_GIFT_CARD_KILLSWITCH,
  SET_E_GIFT_CARD_KILLSWITCH,
  SET_ORDER_STATUS_KILLSWITCH,
  SET_BABY_REGISTRY_KILL_SWITCH,
  SET_IS_AEM_ENABLED_FOR_REGISTRY_KILL_SWITCH,
  SET_CONTEXTUAL_WIDGET_KILLSWITCH,
  SET_PROS_COLLECTION_ENABLED_KILLSWITCH,
  SET_SHOPPING_MODE_FOR_AEM_AS_SITE_ENABLED_KILLSWITCH,
  SET_CREATE_REGISTRY_MANUAL_SURVEY_ENABLED_KILLSWITCH,
  SET_ADDRESS_VALIDATION_KILLSWITCH,
  SET_ADDRESS_LOOKUP_KILLSWITCH,
  SET_IS_BOOXI_WIDGET_ENABLED_KILLSWITCH,
} from '~/types/mutations';
import { httpGetProtectedAPIHeaders, httpUpdateAPIHeaders } from '~/utils/APIUtil';
import { setRegistryDetails } from '~/utils/LocalStorageUtil';
import { SoftSignInValidation } from '~/utils/SoftSignInValidation.js';
import { generateLogin365FingerPrint } from '~/utils/PersistentLoginUtil';

import $http from '@vue-util/http';

const REGISTRY_CONFIG_ENDPOINT = '/regsvc/registry/api/v1/config/get';
const USER_REGISTRY_ENDPOINT = '/regsvc/registry/api/v1/registry/association';
const UPDATE_DEFAULT_REGISTRY_ENDPOINT = '/regsvc/registry/api/v1/registry/defaultRegistry';

export const useBaseStore = defineStore('base', {
  state: () => ({
    giftCardEnabled: false,
    eGiftCardEnabled: false,
    addressLookupEnabled: false,
    orderStatusEnabledKS: false,
    babyRegistryEnabled: false,
    isAEMEnabledForRegistry: false,
    contextualWidgetEnabled: false,
    prosCollectionEnabled: false,
    addressValidationEnabled: false,
    shoppingModeForAemAsSiteEnabled: false,
    createRegistryManualSurveyEnabled: false,
    isBooxiWidgetEnabled: false,
  }),
  actions: {
    // Old Mutations equivalent
    [SET_GIFT_CARD_KILLSWITCH](data) {
      this.giftCardEnabled = data;
    },
    [SET_ADDRESS_LOOKUP_KILLSWITCH](data) {
      this.addressLookupEnabled = data;
    },
    [SET_E_GIFT_CARD_KILLSWITCH](data) {
      this.eGiftCardEnabled = data;
    },
    [SET_ADDRESS_VALIDATION_KILLSWITCH](data) {
      this.addressValidationEnabled = data;
    },
    [SET_ORDER_STATUS_KILLSWITCH](data) {
      this.orderStatusEnabledKS = data;
    },
    [SET_BABY_REGISTRY_KILL_SWITCH](data) {
      this.babyRegistryEnabled = data;
    },
    [SET_IS_AEM_ENABLED_FOR_REGISTRY_KILL_SWITCH](data) {
      this.isAEMEnabledForRegistry = data;
    },
    [SET_CONTEXTUAL_WIDGET_KILLSWITCH](data) {
      this.contextualWidgetEnabled = data;
    },
    [SET_PROS_COLLECTION_ENABLED_KILLSWITCH](data) {
      this.prosCollectionEnabled = data;
    },
    [SET_SHOPPING_MODE_FOR_AEM_AS_SITE_ENABLED_KILLSWITCH](data) {
      this.shoppingModeForAemAsSiteEnabled = data;
    },
    [SET_CREATE_REGISTRY_MANUAL_SURVEY_ENABLED_KILLSWITCH](data) {
      this.createRegistryManualSurveyEnabled = data;
    },
    [SET_IS_BOOXI_WIDGET_ENABLED_KILLSWITCH](data) {
      this.isBooxiWidgetEnabled = data;
    },

    // Old Actions equivalent
    async [GET_KILLSWITCH_CONFIG](params) {
      try {
        let payload = {};
        if (params.regId) {
          payload = {
            macys_registryid: params.regId,
          };
        }
        const login365FingerPrint = await generateLogin365FingerPrint();
        const response = await $http.get(`${REGISTRY_CONFIG_ENDPOINT}?configType=KillSwitch&orgId=${params.orgId}`, {
          headers: httpGetProtectedAPIHeaders({ ...payload, ...login365FingerPrint }),
        });

        const isError = response?.data?.errors || response?.data?.error;
        if (isError) {
          this[SET_E_GIFT_CARD_KILLSWITCH](false);
          this[SET_GIFT_CARD_KILLSWITCH](false);
          this[SET_ORDER_STATUS_KILLSWITCH](false);
          this[SET_BABY_REGISTRY_KILL_SWITCH](false);
          this[SET_ADDRESS_LOOKUP_KILLSWITCH](false);
        } else {
          response.data.registryInfo.forEach((item) => {
            if (item.registryConfigKey?.toUpperCase() === 'BABYREGISTRY' && item.registryConfigValue?.toUpperCase() === 'TRUE') {
              this[SET_BABY_REGISTRY_KILL_SWITCH](true);
            } else if (item.registryConfigKey?.toUpperCase() === 'EGC' && item.registryConfigValue?.toUpperCase() === 'TRUE') {
              this[SET_E_GIFT_CARD_KILLSWITCH](true);
            } else if (item.registryConfigKey?.toUpperCase() === 'GIFTCARD' && item.registryConfigValue?.toUpperCase() === 'TRUE') {
              this[SET_GIFT_CARD_KILLSWITCH](true);
            } else if (item.registryConfigKey?.toUpperCase() === 'ORDERSTATUS' && item.registryConfigValue?.toUpperCase() === 'TRUE') {
              this[SET_ORDER_STATUS_KILLSWITCH](true);
            } else if (item.registryConfigKey?.toUpperCase() === 'ISAEMENABLEDFORREGISTRY' && item.registryConfigValue?.toUpperCase() === 'TRUE') {
              this[SET_IS_AEM_ENABLED_FOR_REGISTRY_KILL_SWITCH](true);
            } else if (item.registryConfigKey?.toUpperCase() === 'CONTEXTUALWIDGETENABLED' && item.registryConfigValue?.toUpperCase() === 'TRUE') {
              this[SET_CONTEXTUAL_WIDGET_KILLSWITCH](true);
            } else if (item.registryConfigKey?.toUpperCase() === 'PROSCOLLECTIONENABLED' && item.registryConfigValue?.toUpperCase() === 'TRUE') {
              this[SET_PROS_COLLECTION_ENABLED_KILLSWITCH](true);
            } else if (
              item.registryConfigKey?.toUpperCase() === 'SHOPPINGMODEFORAEMASSITEENABLED' &&
              item.registryConfigValue?.toUpperCase() === 'TRUE'
            ) {
              this[SET_SHOPPING_MODE_FOR_AEM_AS_SITE_ENABLED_KILLSWITCH](true);
            } else if (
              item.registryConfigKey?.toUpperCase() === 'CREATEREGISTRYMANUALSURVEYENABLED' &&
              item.registryConfigValue?.toUpperCase() === 'TRUE'
            ) {
              this[SET_CREATE_REGISTRY_MANUAL_SURVEY_ENABLED_KILLSWITCH](true);
            } else if (item.registryConfigKey?.toUpperCase() === 'ADDRESSVALIDATIONENABLED' && item.registryConfigValue?.toUpperCase() === 'TRUE') {
              this[SET_ADDRESS_VALIDATION_KILLSWITCH](true);
            } else if (item.registryConfigKey?.toUpperCase() === 'ADDRESSLOOKUPENABLED' && item.registryConfigValue?.toUpperCase() === 'TRUE') {
              this[SET_ADDRESS_LOOKUP_KILLSWITCH](true);
            } else if (item.registryConfigKey?.toUpperCase() === 'ISBOOXIWIDGETENABLED' && item.registryConfigValue?.toUpperCase() === 'TRUE') {
              this[SET_IS_BOOXI_WIDGET_ENABLED_KILLSWITCH](true);
            }
          });
        }
      } catch {
        this[SET_E_GIFT_CARD_KILLSWITCH](false);
        this[SET_GIFT_CARD_KILLSWITCH](false);
        this[SET_ADDRESS_LOOKUP_KILLSWITCH](false);
        this[SET_ORDER_STATUS_KILLSWITCH](false);
        this[SET_BABY_REGISTRY_KILL_SWITCH](false);
        this[SET_CONTEXTUAL_WIDGET_KILLSWITCH](false);
        this[SET_PROS_COLLECTION_ENABLED_KILLSWITCH](false);
        this[SET_SHOPPING_MODE_FOR_AEM_AS_SITE_ENABLED_KILLSWITCH](false);
        this[SET_CREATE_REGISTRY_MANUAL_SURVEY_ENABLED_KILLSWITCH](false);
        this[SET_ADDRESS_VALIDATION_KILLSWITCH](false);
      }
    },
    async [GET_USER_REGISTRIES](payload) {
      const { fromLanding } = payload;
      delete payload.fromLanding;
      try {
        const login365FingerPrint = await generateLogin365FingerPrint();
        const response = await $http.post(`${USER_REGISTRY_ENDPOINT}`, payload, {
          headers: httpUpdateAPIHeaders(payload, login365FingerPrint),
        });
        const isError = response?.data?.errors || response?.data?.error;
        if (!isError) {
          setRegistryDetails(response?.data?.registryInfo);
        } else if (!fromLanding) {
          SoftSignInValidation(isError);
        }
      } catch (error) {
        if (!fromLanding) {
          SoftSignInValidation(error);
        }
      }
    },
    async [UPDATE_DEFAULT_REGISTRY](payload) {
      try {
        const login365FingerPrint = await generateLogin365FingerPrint();
        await $http.put(`${UPDATE_DEFAULT_REGISTRY_ENDPOINT}`, payload, {
          headers: httpUpdateAPIHeaders(payload, login365FingerPrint),
        });
      } catch (error) {
        SoftSignInValidation(error);
      }
    },
  },
});
